import React from "react";
import './ConflictSummary.scss'
// Reuse step-specific styles for consistent look
import '../MakePlan/Recap/Recap.scss'
import '../MakePlan/SuggestedIdeas.scss'
import '../MakePlan/MakePlan/MakePlan.scss'
import AnimalsThumbnail from "../AnimalsThumbnail/AnimalsThumbnail";
import {buildJourneyFromStepIds} from "../../pages/Journey/JourneyFlowItems";
import {useTranslation} from "react-i18next";
import moment from "moment";
import {getCardSides} from "../../utils/helpers";

const ConflictSummary = ({
      mirrorID,
      relationship,
      summaryPageRef,
      summaryFeltImgTempRef,
      summaryFeltImgRef,
      html2canvasRef,
      copyDivToClipboard,
      isAlignmentStep = false
  }) => {
  const {t} = useTranslation();

  if (!relationship || !mirrorID) return null;

  const {
    mySideMirrors,
    name,
    partnerName,
    animalId,
    partnerAnimalId,
    gender,
    partnerGender
  } = relationship;

  const mySideMirror = mySideMirrors.find((m) => m.id === mirrorID);
  const jp = mySideMirror?.journeyProps || {};

  const {createdAt} = mySideMirror;
  const formattedCreatedAt = moment(createdAt).format('DD/MM/yyyy');


  const mirrorName =
    mySideMirror?.mirrorName ||
    mySideMirror?.journeyProps?.AISummary?.mirrorName ||
    mySideMirror?.AISummary?.mirrorName ||
    null;

  const mirrorDescription =
    mySideMirror?.mirrorDescription ||
    mySideMirror?.journeyProps?.AISummary?.mirrorDescription ||
    mySideMirror?.AISummary?.mirrorDescription ||
    null;

  // Unified suggestions (handles both legacy and new format)
  const aiSummaryObj = mySideMirror?.journeyProps?.AISummary || {};
  const legacyUser = Array.isArray(aiSummaryObj.userSuggestions)
    ? aiSummaryObj.userSuggestions.map((d) => ({ description: d, type: 'userSuggestion' }))
    : [];
  const newSug = Array.isArray(aiSummaryObj.suggestions) ? aiSummaryObj.suggestions : [];
  // const unifiedSuggestions = [...legacyUser, ...newSug];

  // Build dynamic flow from the mirror's step ids so summary follows product changes
  const journeyFlow = buildJourneyFromStepIds(mySideMirror?.journeyStepIds || []);

  return <div className={"summary-container" + (isAlignmentStep ? " alignment" : "")}>
    <div className={"summary__page summary-section" + (isAlignmentStep ? " alignment" : "")} ref={summaryPageRef}>
      {!isAlignmentStep && (
      <div className="summary__header summary-padding">
        <p className="summary__title--names">{t(`homepage.meAndPartner`, {
          name: name,
          partnerName: partnerName
        })}</p>
        <h2 className="summary__title--mirrorName">{mirrorName || t(`mirroring.defaultJourneyName`)}</h2>
        <p className="summary__title--date">
          {t("mirroring.mirrirCreatedOn") + " " + formattedCreatedAt}</p>
        {!isAlignmentStep && (
          <div className="feltImg-container">
            {summaryFeltImgTempRef && <div className="feltImgTemp breathAnimation" ref={summaryFeltImgTempRef}></div>}
            {summaryFeltImgRef && <div className="feltImg fade-in" ref={summaryFeltImgRef}></div>}
            <div className={html2canvasRef ? 'html2canvas' : ''} ref={html2canvasRef}>
              <AnimalsThumbnail
                thumbnailClassName={"summary--thumbnail"}
                animalId={animalId}
                partnerAnimalId={partnerAnimalId}
                name={name}
                partnerName={partnerName}
              />
            </div>
          </div>
        )}
      </div>
      )}
      {mirrorDescription && !isAlignmentStep && (
        <div className="summary__mirror-description summary-padding">{mirrorDescription}</div>
      )}
      <div>
        {journeyFlow.map((journeyItem, jIdx) => {
            // In alignment step, include only the first two journey items
            if (isAlignmentStep && jIdx > 1) return null;
            return (
              <React.Fragment key={`ji-${jIdx}`}>
                <h1 className="summary__journeyName summary-padding">{t(journeyItem.title, {
                  name: name,
                  partnerName: partnerName
                })}</h1>
                
                {!isAlignmentStep && jIdx === 2 && (
                  <div className="summary__ai-suggestions summary-padding">
                    {/* Recap problems (only) */}
                    {Array.isArray(aiSummaryObj.relationshipProblems) && aiSummaryObj.relationshipProblems.length > 0 && (
                      <div className="summary__problems recapStep">
                        <p className="summary__sectionTitle"><strong>{t('recap.relationshipProblems')}</strong></p>
                        <div className="problems-list">
                          {aiSummaryObj.relationshipProblems.map((p, pIdx) => {
                            const dot = p?.type === 'crossedTheRedLine' ? '🔴' : (p?.type === 'notCrossedTheRedLine' ? '🟡' : '⚫');
                            return (
                              <div key={`rel-problem-${pIdx}`} className="problem-item">
                                <div className="problem-header">
                                  <span className="problem-emoji">{dot}</span>
                                  <span className="problem-name">{p?.name || ''}</span>
                                </div>
                                {p?.description && <div className="problem-description">{p.description}</div>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Selected idea */}
                    {(() => {
                      const selected = (Array.isArray(aiSummaryObj.suggestions) ? aiSummaryObj.suggestions : []).find(s => s && s.selected);
                      if (!selected) return null;
                      return (
                        <div className="summary__selectedIdea ideasGallery">
                          <p className="summary__sectionTitle"><strong>{t('MakePlan.idea-title')}</strong></p>
                          <div className="ideas-grid">
                            <div className={"idea-card idea-card--selected"}>
                              <div className="idea-card-text-container">
                                {selected.emoji && <div className="idea-card-emoji">{selected.emoji}</div>}
                                {selected.title && <div className="idea-card-title">{selected.title}</div>}
                                {selected.description && <div className="idea-card-description">{selected.description}</div>}
                                {selected.why && <div className="idea-card-why">{selected.why}</div>}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Action plan for selected idea */}
                    {(() => {
                      const selected = (Array.isArray(aiSummaryObj.suggestions) ? aiSummaryObj.suggestions : []).find(s => s && s.selected);
                      const plan = selected && selected.plan;
                      if (!plan) return null;
                      return (
                        <div className="summary__plan">
                          <p className="summary__sectionTitle"><strong>{t('MakePlan.make-plan-title')}</strong></p>
                          <div className="plan-content">
                            <div className="plan-header">
                              {plan.planName && <p className="plan-content-name">{plan.planName}</p>}
                              {plan.planDescription && <p className="plan-content-description">{plan.planDescription}</p>}
                              {plan.planGoal && (
                                <p className="plan-content-goal"><strong>{t('MakePlan.goal')}:</strong> {plan.planGoal}</p>
                              )}
                            </div>
                            {Array.isArray(plan.planTodoList) && plan.planTodoList.length > 0 && (
                              <div className="plan-todo">
                                <div className="plan-content-todo-list-title">{t('MakePlan.todoList-title')}</div>
                                <ol className="plan-content-todo-list">
                                  {plan.planTodoList.map((item, idx) => (
                                    <li className="plan-content-todo" key={`todo-${idx}`}>{item}</li>
                                  ))}
                                </ol>
                              </div>
                            )}
                            {Array.isArray(plan.planWhy) && plan.planWhy.length > 0 && (
                              <div className="plan-why">
                                <div className="plan-content-why-title">{t('MakePlan.whyTitle')}</div>
                                <ul>
                                  {plan.planWhy.map((w, idx) => (
                                    <li className="plan-content-why" key={`why-${idx}`}>{w}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {plan.planWhyExplanation && (
                              <p className="plan-content-explanation"><strong>{t('MakePlan.explanation')}:</strong> {plan.planWhyExplanation}</p>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
                {(!(!isAlignmentStep && jIdx === 2)) && journeyItem.steps.map((step, sIdx) => {
                  if (!step.stepItems) return null;
                  const itemsToRender = step.stepItems.filter((stepItem) => stepItem && stepItem.type !== 'Intro' && jp[stepItem.state]);
                  if (itemsToRender.length === 0) return null;
                  return (
                    <div key={`st-${jIdx}-${sIdx}`} className="summary__step summary-padding">
                      {itemsToRender.map((stepItem, iIdx) => {
                        return (
                          <div key={`si-${jIdx}-${sIdx}-${iIdx}`} className="summary__stepItem">
                            <p className="summary__stepName"><strong><span className="hidden">*</span>{t(stepItem.label, { name: name, partnerName: partnerName })}<span className="hidden">*</span></strong></p>
                            {stepItem.type === 'TextArea' ? (
                              <div className="summary__textArea">{jp[stepItem.state]}</div>
                            ) : stepItem.type === 'FeelsCard' ? (
                              <div className="summary-cards summary_cards--feel">
                                {(jp[stepItem.state] || []).map((card, cIdx) => {
                                  const isPositive = card.isPositive;
                                  const isReversed = card.modifier === 'isReversed';
                                  const effectivePositive = isReversed ? !isPositive : isPositive;
                                  return (
                                    <table key={`feel-${jIdx}-${sIdx}-${iIdx}-${cIdx}`} className={'summary_card card--feel ' + (effectivePositive ? ' card--feel_pleasant' : ' card--feel_unpleasant')} cellSpacing="0" cellPadding="0" border="0">
                                      <tbody>
                                        <tr>
                                          <td className="card__emoji card--cell" width="auto">{card.emoji}</td>
                                          <td className="card__modifier card--cell" width="auto">{(card.modifier === `isReduced` && t(`card.alittle`) ? t(`card.alittle`) : '') + (card.modifier === `isReversed` && t(`card.not`) ? t(`card.not`) : '')}</td>
                                          <td className="card--cell" width="auto">{t(`cards.FEEL.${card.title}.title`)}</td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  );
                                })}
                              </div>
                            ) : stepItem.type === 'NeedCard' ? (
                              <div className="summary-cards summary_cards--need">
                                {(jp[stepItem.state] || []).map((card, cIdx) => (
                                  <table key={`need-${jIdx}-${sIdx}-${iIdx}-${cIdx}`} className="summary_card card--need" cellSpacing="0" cellPadding="0" border="0">
                                    <tbody>
                                      <tr>
                                        <td className="card__emoji card--cell" width="auto">{card.emoji}</td>
                                        <td className="card__modifier card--cell" width="auto">{(card.modifier === `isReduced` && t(`card.alittle`) ? t(`card.alittle`) : '') + (card.modifier === `isReversed` && t(`card.not`) ? t(`card.not`) : '')}</td>
                                        <td className="card--cell" width="auto">{t(`cards.${card.cardType || 'NEED'}.${card.title}.title`)}</td>
                                      </tr>
                                    </tbody>
                                  </table>
                                ))}
                              </div>
                            ) : stepItem.type === 'HappenedCard' ? (
                              <div className="summary-cards summary_cards--happened">
                                {(jp[stepItem.state] || []).map((card, cIdx) => {
                                  const { sourceGender, targetName, sourceName } = getCardSides({ switchSides: card.switchSides, name, partnerName, gender, partnerGender });
                                  return (
                                    <table key={`happened-${jIdx}-${sIdx}-${iIdx}-${cIdx}`} className="summary_card summary_card--happened" cellSpacing="0" cellPadding="0" border="0">
                                      <tbody>
                                        <tr>
                                          <td className="card__emoji card--cell" width="auto">{card.emoji}</td>
                                          <td className="card__name card--cell" width="auto">{sourceName}</td>
                                          <td className="card__modifier card--cell" width="auto">{(card.modifier === `isReduced` && t(`card.alittle`) ? t(`card.alittle`) : '') + (card.modifier === `isReversed` && t(`card.not`) ? t(`card.not`) : '')}</td>
                                          <td className="card--cell" width="auto">{t(`cards.HAPPENED.${card.title}.title`, { context: sourceGender })}</td>
                                          <td className="card--cell" width="auto">{t(`cards.HAPPENED.${card.title}.prefix`)}</td>
                                          <td className="card__name card--cell" width="auto">{targetName}</td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  );
                                })}
                              </div>
                            ) : null}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </React.Fragment>
            )
          }
        )}
      </div>
      <br/><br/>
      {!isAlignmentStep && (
        <div className="summary-footer">
          <p>{t('general.createdWithFelt')}</p>
          <a href="https://www.catbears.com/felt">https://www.catbears.com/felt</a>
        </div>
      )}
    </div>
  </div>
}

export default ConflictSummary